import { PhoneNumberOwnerType, PhoneNumberType } from '../../../../common/enums.js';
import { t } from '../../../../common/i18n/index.js';
import { getNextAllowedDay, getNormalizedDateFromDateString } from '../../../../common/utils/dateUtils.js';
import { SelectPhoneNumberCoCo } from '../../../SelectPhoneNumber/SelectPhoneNumberCoCo.js';
import type { PhoneNumberOwner } from '../../../../common/types/subscription.js';
import type { ValidatedPhoneNumber } from '../../../../common/utils/phoneNumberUtils.js';
import type { SubscriptionConfigFormState } from '../../utils/subscriptionConfigurationUtilsCoCo.js';
import type { ProductConfigSchema } from '../useProductConfigSchemaCoCo.js';

export interface NumberSelectionSectionProps {
  schema: ProductConfigSchema;
  cartItemInstanceId: string;
  companyName: string;
  formState: SubscriptionConfigFormState;
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  onLoadMoreNumbers: () => void;
  disabledPhoneNumbers: string[];
  initialPhoneNumber?: string;
  validatedPhoneNumbers: ValidatedPhoneNumber[];
  holidays: Date[];
}

/**
 * Renders the phone number selection section.
 * Must be rendered inside a FormProvider for the inner subscription config form.
 */
export const NumberSelectionSectionCoCo = ({
  schema,
  cartItemInstanceId,
  companyName,
  formState,
  phoneNumbers,
  phoneNumbersLoading,
  onLoadMoreNumbers,
  disabledPhoneNumbers,
  initialPhoneNumber,
  validatedPhoneNumbers,
  holidays,
}: NumberSelectionSectionProps) => {
  if (!schema.needsPhoneNumber) {
    return null;
  }

  const phoneNumberOwner: PhoneNumberOwner | undefined =
    formState.phoneNumberType === PhoneNumberType.EXISTING
      ? {
          ownerType: formState.differentDonor ? PhoneNumberOwnerType.PERSON : PhoneNumberOwnerType.COMPANY,
          personEmail: formState.donorEmail,
          permitDonoringOnFixedContract: formState.fixedTermContract,
        }
      : undefined;

  return (
    <SelectPhoneNumberCoCo
      cartItemInstanceId={cartItemInstanceId}
      companyName={companyName}
      datePickerLabel={t.MYX9('Pick a date')}
      datePickerLabelOptionalText=""
      disabledPhoneNumbers={disabledPhoneNumbers}
      existingPhoneNumberLabel={t.MYX7('Fill in existing phonenumber')}
      getNextAllowedDay={getNextAllowedDay}
      holidays={holidays}
      initiallySelectedPhoneNumber={formState.newPhoneNumber || initialPhoneNumber}
      onSelectUseExistingPhoneNumber={() => {}}
      personOwnerEmailLabel={t.MYX8('Fill in email address')}
      phoneNumbers={phoneNumbers}
      phoneNumbersLoading={phoneNumbersLoading}
      phoneNumberOwner={phoneNumberOwner}
      selectedPhoneNumber={
        formState.phoneNumberType === PhoneNumberType.EXISTING
          ? {
              type: PhoneNumberType.EXISTING,
              existingPhoneNumber: formState.existingPhoneNumber,
              transferDate: (() => {
                try {
                  return formState.transferDate
                    ? getNormalizedDateFromDateString(formState.transferDate)
                    : getNextAllowedDay(holidays);
                } catch {
                  return getNextAllowedDay(holidays);
                }
              })(),
            }
          : {
              type: PhoneNumberType.NEW,
              newPhoneNumber: formState.newPhoneNumber,
            }
      }
      validatedPhoneNumbers={validatedPhoneNumbers}
      onLoadMore={onLoadMoreNumbers}
      showNewNumber={schema.needsPhoneNumber}
    />
  );
};
